import { Alert, Paper, Stack, Typography } from '@mui/material';
import type { WorkspaceAction } from './workspaceApi';

const actionNames: Record<WorkspaceAction['kind'], string> = {
  'campaign-summary': 'Read campaign summary',
  'campaign-start': 'Start campaign',
  'campaign-stop': 'Stop campaign',
  'campaign-rename': 'Rename campaign',
  'webhook-dispatch': 'Dispatch configured webhooks',
};
const record = (value: unknown): Record<string, unknown> | undefined =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;

/** Render only the reviewed target fields, never arbitrary provider/configuration payloads. */
export const OperationActionReview = ({
  actions,
  snapshots,
}: {
  actions: WorkspaceAction[];
  snapshots: unknown[];
}) => (
  <Stack gap={2}>
    {actions.map((action, index) => {
      const candidate = record(snapshots[index]);
      const snapshot = candidate?.id === action.campaignId ? candidate : undefined;
      const title = typeof snapshot?.title === 'string' ? snapshot.title : undefined;
      const status = typeof snapshot?.status === 'string' ? snapshot.status : undefined;
      return (
        <Paper variant="outlined" sx={{ p: 2 }} key={`${index}:${action.kind}`}>
          <Stack gap={1}>
            <Typography variant="subtitle2">
              {index + 1}. {actionNames[action.kind]}
            </Typography>
            <Typography variant="body2">Campaign: {title || action.campaignId}</Typography>
            {title && (
              <Typography variant="caption" color="text.secondary">
                ID: {action.campaignId}
              </Typography>
            )}
            {action.kind === 'campaign-rename' ? (
              <>
                <Typography variant="body2">From: {title || 'Saved name unavailable'}</Typography>
                <Typography variant="body2">To: {action.title}</Typography>
              </>
            ) : action.kind === 'campaign-start' || action.kind === 'campaign-stop' ? (
              <>
                <Typography variant="body2">From: {status || 'Saved state unavailable'}</Typography>
                <Typography variant="body2">
                  Requested state: {action.kind === 'campaign-start' ? 'Active' : 'Stopping'}
                </Typography>
                {action.kind === 'campaign-stop' && (
                  <Typography variant="body2" color="text.secondary">
                    Stop scheduling new calls and let active calls finish.
                  </Typography>
                )}
              </>
            ) : action.kind === 'webhook-dispatch' ? (
              <>
                <Typography variant="body2">
                  Call:{' '}
                  {action.callId ||
                    (typeof snapshot?.callId === 'string'
                      ? snapshot.callId
                      : 'Resolved from the triggering event')}
                </Typography>
                <Typography variant="body2">
                  {Array.isArray(snapshot?.webhooks)
                    ? `${snapshot.webhooks.length} enabled webhook destinations reviewed`
                    : 'Enabled campaign destinations will be checked at execution'}
                </Typography>
                <Alert severity="info">
                  This action sends data to the configured external destinations.
                </Alert>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Read the saved campaign state without changing it.
              </Typography>
            )}
          </Stack>
        </Paper>
      );
    })}
  </Stack>
);
